import { articles } from '@/data/articles';
import { PESTS } from '@/data/pests';
import { REMEDIES } from '@/data/remedies';
import { BENEFICIAL_INSECTS, CATEGORY_LABELS as BENEFICIAL_CATEGORY_LABELS } from '@/data/beneficialInsects';

// ── Types ──────────────────────────────────────────────────────────────────────

export type LearnItemType = 'article' | 'pest' | 'remedy' | 'beneficial' | 'guide' | 'tool';

export interface LearnSearchItem {
  id: string;
  title: string;
  description: string;
  href: string;
  type: LearnItemType;
  typeLabel: string;
  category: string;
  tags: string[];
  keywords: string[];
  bodyText: string;
  priority: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function trunc(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) : s;
}

// ── Label maps ─────────────────────────────────────────────────────────────────

const ARTICLE_CATEGORY_LABELS: Record<string, string> = {
  'getting-started':       'Getting Started',
  'soil-and-compost':      'Soil & Compost',
  'companion-planting':    'Companion Planting',
  'pest-and-disease':      'Pest & Disease',
  'watering-and-feeding':  'Watering & Feeding',
  'seasonal-growing':      'Seasonal Growing',
  'harvesting-and-storage': 'Harvesting & Storage',
  'fruit-trees':           'Fruit Trees',
};

const PEST_TYPE_LABELS: Record<string, string> = {
  insect:        'Insect',
  disease:       'Disease',
  fungal:        'Fungal Disease',
  animal:        'Animal Pest',
  deficiency:    'Deficiency',
  physiological: 'Physiological',
  other:         'Pest',
};

const REMEDY_CATEGORY_LABELS: Record<string, string> = {
  'pest-control': 'Pest Control',
  fungal:         'Fungal Treatment',
  'soil-health':  'Soil Health',
  nutrients:      'Nutrients',
  general:        'Remedy',
};

// ── Static guide + tool entries ────────────────────────────────────────────────

const STATIC_ITEMS: LearnSearchItem[] = [
  // Hub pages
  {
    id: 'pests-hub',
    title: 'Pest & Disease Identifier',
    description: 'Identify garden pests and diseases by symptom. Browse problems and find organic solutions.',
    href: '/learn/pests',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Pest & Disease',
    tags: ['pests', 'disease', 'identification', 'organic', 'symptoms', 'diagnosis'],
    keywords: ['pest', 'bug', 'insect', 'disease', 'fungal', 'identify', 'diagnose', 'problem'],
    bodyText: '',
    priority: 8,
  },
  {
    id: 'beneficial-hub',
    title: 'Garden Allies - Beneficial Insects',
    description: 'Meet the insects, birds, and soil creatures that control pests and pollinate your garden.',
    href: '/learn/beneficial',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Beneficial Insects',
    tags: ['pollinators', 'predators', 'beneficial', 'insects', 'allies', 'garden allies'],
    keywords: ['beneficial', 'pollinator', 'predator', 'ladybug', 'lacewing', 'bee', 'hoverfly'],
    bodyText: '',
    priority: 7,
  },
  {
    id: 'remedies-hub',
    title: 'Garden Remedies',
    description: 'Organic sprays, soil treatments, and pest controls. Wildlife-friendly solutions for common garden problems.',
    href: '/learn/remedies',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Remedies',
    tags: ['remedies', 'organic', 'spray', 'treatment', 'neem', 'copper', 'kaolin'],
    keywords: ['remedy', 'treatment', 'spray', 'organic', 'neem', 'copper', 'kaolin', 'solution'],
    bodyText: '',
    priority: 7,
  },
  // Deep guides
  {
    id: 'beginners-path',
    title: "Beginner's Path",
    description: 'A season-by-season guide to your first garden. Start here if you are new to growing food.',
    href: '/learn/beginners-path',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Getting Started',
    tags: ['beginner', 'first garden', 'getting started', 'seasons', 'basics', 'start here'],
    keywords: ['beginner', 'new gardener', 'start', 'first garden', 'basics', 'getting started'],
    bodyText: '',
    priority: 6,
  },
  {
    id: 'permaculture',
    title: 'Permaculture Principles',
    description: 'The 12 permaculture principles explained for home gardeners. Design with nature, not against it.',
    href: '/learn/permaculture',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Permaculture',
    tags: ['permaculture', 'design', 'principles', 'ecology', 'systems', 'zones'],
    keywords: ['permaculture', 'ecological design', 'systems thinking', 'guild', 'polyculture', 'zone'],
    bodyText: '',
    priority: 5,
  },
  {
    id: 'soil-health',
    title: 'Soil Health',
    description: 'Healthy soil is a living community. Learn to build and feed your soil biology for long-term garden fertility.',
    href: '/learn/soil',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Soil',
    tags: ['soil', 'compost', 'biology', 'fungi', 'organic matter', 'no-till', 'earthworm'],
    keywords: ['soil', 'compost', 'earthworm', 'mycorrhizal', 'no-till', 'organic matter', 'amendment', 'fertility'],
    bodyText: '',
    priority: 6,
  },
  {
    id: 'native-plants',
    title: 'Native Plants',
    description: 'Why native plants matter for pollinators, soil, water, and the wider web of life in your garden.',
    href: '/learn/native-plants',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Native Plants',
    tags: ['native', 'pollinators', 'ecology', 'wildlife', 'habitat', 'biodiversity'],
    keywords: ['native plant', 'indigenous', 'pollinator habitat', 'wildlife garden', 'biodiversity', 'local'],
    bodyText: '',
    priority: 5,
  },
  {
    id: 'guilds',
    title: 'Interplanting and Plant Guilds',
    description: 'Design polyculture plant communities that support and protect each other. Beyond companion planting.',
    href: '/learn/guilds',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Companion Planting',
    tags: ['guilds', 'interplanting', 'polyculture', 'companion planting', 'food forest', 'three sisters'],
    keywords: ['guild', 'interplanting', 'polyculture', 'food forest', 'companion', 'three sisters', 'guild planting'],
    bodyText: '',
    priority: 5,
  },
  {
    id: 'indigenous-land-care',
    title: 'Indigenous Land Care',
    description: 'Thousands of years of reciprocal land stewardship. Principles and practices that predate modern agriculture.',
    href: '/learn/indigenous-land-care',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Land Care',
    tags: ['indigenous', 'land care', 'stewardship', 'traditional', 'ecology', 'reciprocal'],
    keywords: ['indigenous', 'traditional knowledge', 'land stewardship', 'reciprocal', 'first nations'],
    bodyText: '',
    priority: 4,
  },
  {
    id: 'natural-inputs',
    title: 'Natural Inputs Guide',
    description: 'What to feed your soil, how to manage pests without harm, and why the inputs you choose matter.',
    href: '/learn/natural-inputs',
    type: 'guide',
    typeLabel: 'Guide',
    category: 'Soil & Feeding',
    tags: ['natural inputs', 'organic feeding', 'compost tea', 'fish emulsion', 'amendments', 'liquid feed'],
    keywords: ['natural input', 'organic fertilizer', 'compost tea', 'fish emulsion', 'kelp', 'amendment', 'liquid feed', 'feed'],
    bodyText: '',
    priority: 5,
  },
  // Tools
  {
    id: 'seed-starting-calculator',
    title: 'Seed Starting Date Calculator',
    description: 'Calculate when to start seeds indoors, when to transplant, and when to direct sow - based on your last frost date.',
    href: '/seed-starting-calculator',
    type: 'tool',
    typeLabel: 'Tool',
    category: 'Tool',
    tags: ['seed starting', 'calculator', 'frost date', 'planting dates', 'transplant', 'indoor sowing'],
    keywords: ['seed start', 'when to plant', 'frost date', 'transplant date', 'indoor sowing', 'start seeds', 'germination'],
    bodyText: '',
    priority: 8,
  },
  {
    id: 'fruit-tree-planner',
    title: 'Fruit Tree Planner',
    description: 'Plan fruit tree pollination, compare compatible varieties, and understand bloom timing for apples, pears, plums, cherries, and blueberries.',
    href: '/tools/fruit-tree-planner',
    type: 'tool',
    typeLabel: 'Tool',
    category: 'Tool',
    tags: ['fruit tree', 'pollination', 'apple', 'pear', 'plum', 'cherry', 'blueberry', 'variety'],
    keywords: ['fruit tree', 'pollination', 'variety', 'compatibility', 'apple', 'pear', 'cherry', 'plum', 'bloom time', 'cross pollination'],
    bodyText: '',
    priority: 8,
  },
  {
    id: 'soil-calculator',
    title: 'Soil Volume Calculator',
    description: 'Calculate how much soil, compost, or mulch you need for raised beds, in-ground gardens, and containers.',
    href: '/tools/soil-calculator',
    type: 'tool',
    typeLabel: 'Tool',
    category: 'Tool',
    tags: ['soil calculator', 'raised bed', 'volume', 'compost', 'mulch', 'container', 'cubic feet'],
    keywords: ['soil volume', 'raised bed', 'cubic feet', 'how much soil', 'bags', 'container', 'bulk yards'],
    bodyText: '',
    priority: 7,
  },
  {
    id: 'garden-templates',
    title: 'Garden Templates',
    description: 'Pre-designed garden plans for beginners and experienced gardeners - pizza garden, salad garden, tea garden, pollinator border, and containers.',
    href: '/templates',
    type: 'tool',
    typeLabel: 'Tool',
    category: 'Tool',
    tags: ['templates', 'garden plan', 'layout', 'beginner', 'pizza garden', 'salad garden', 'pollinator'],
    keywords: ['template', 'garden plan', 'layout', 'design', 'pizza garden', 'salad garden', 'pollinator border', 'plan'],
    bodyText: '',
    priority: 6,
  },
];

// ── Build the search index ─────────────────────────────────────────────────────

function buildIndex(): LearnSearchItem[] {
  const items: LearnSearchItem[] = [];

  // Articles
  for (const a of articles) {
    const bodyRaw = stripHtml(a.content);
    items.push({
      id: `article-${a.slug}`,
      title: a.title,
      description: a.excerpt,
      href: `/learn/articles/${a.slug}`,
      type: 'article',
      typeLabel: 'Article',
      category: ARTICLE_CATEGORY_LABELS[a.category] ?? a.category,
      tags: [
        a.category,
        ...(a.relatedPlantIds ?? []),
        ...(a.relatedPestIds ?? []),
      ],
      keywords: [
        ...(a.relatedPlantIds ?? []),
        ...(a.relatedPestIds ?? []),
      ],
      bodyText: trunc(bodyRaw, 3000),
      priority: a.tier === 'free' ? 5 : 3,
    });
  }

  // Pests (skip drafts)
  for (const p of PESTS) {
    if (p.status === 'draft') continue;
    const bodyText = [
      ...p.symptoms,
      ...p.affectedPlants,
      p.lifecycle ?? '',
      p.spread ?? '',
    ].join(' ');
    items.push({
      id: `pest-${p.id}`,
      title: p.name,
      description: p.description,
      href: `/learn/pests/${p.id}`,
      type: 'pest',
      typeLabel: PEST_TYPE_LABELS[p.type] ?? 'Pest',
      category: PEST_TYPE_LABELS[p.type] ?? 'Pest',
      tags: [
        ...p.tags,
        p.type,
        ...(p.scientificName ? [p.scientificName] : []),
      ],
      keywords: [
        ...p.affectedPlants,
        ...p.symptoms.slice(0, 5),
      ],
      bodyText: trunc(bodyText, 1200),
      priority: 3,
    });
  }

  // Remedies
  for (const r of REMEDIES) {
    items.push({
      id: `remedy-${r.id}`,
      title: r.name,
      description: r.shortDescription,
      href: `/learn/remedies/${r.id}`,
      type: 'remedy',
      typeLabel: REMEDY_CATEGORY_LABELS[r.category] ?? 'Remedy',
      category: REMEDY_CATEGORY_LABELS[r.category] ?? 'Remedy',
      tags: [...r.tags, ...r.problemTags, r.category],
      keywords: [...r.usedFor],
      bodyText: trunc(`${r.whatItIs} ${r.whatItDoes}`, 800),
      priority: 4,
    });
  }

  // Beneficial insects
  for (const b of BENEFICIAL_INSECTS) {
    items.push({
      id: `beneficial-${b.id}`,
      title: b.name,
      description: b.shortDescription,
      href: `/learn/beneficial/${b.id}`,
      type: 'beneficial',
      typeLabel: BENEFICIAL_CATEGORY_LABELS[b.category] ?? 'Garden Ally',
      category: BENEFICIAL_CATEGORY_LABELS[b.category] ?? 'Garden Ally',
      tags: [b.category, ...b.helpsControl, ...b.attractWith.slice(0, 5)],
      keywords: [...b.helpsControl, ...b.controlsPests],
      bodyText: trunc(b.whyYouWantThem, 600),
      priority: 3,
    });
  }

  // Static guides and tools
  items.push(...STATIC_ITEMS);

  return items;
}

// Built once at module load time - safe since all source data is static
const LEARN_INDEX = buildIndex();

// ── Scoring ────────────────────────────────────────────────────────────────────

function scoreItem(item: LearnSearchItem, queryNorm: string, tokens: string[]): number {
  let score = 0;
  const titleLow = item.title.toLowerCase();
  const descLow = item.description.toLowerCase();
  const bodyLow = item.bodyText.toLowerCase();
  const allKeywords = [
    ...item.tags.map(t => t.toLowerCase()),
    ...item.keywords.map(k => k.toLowerCase()),
  ];

  // Title matching (highest weight)
  if (titleLow === queryNorm) {
    score += 100;
  } else if (titleLow.startsWith(queryNorm)) {
    score += 80;
  } else if (titleLow.includes(queryNorm)) {
    score += 60;
  }

  // Token matches in title
  const titleHits = tokens.filter(t => titleLow.includes(t)).length;
  score += titleHits * 20;

  // Tags and keywords
  if (allKeywords.some(k => k === queryNorm || k.includes(queryNorm))) score += 25;
  for (const t of tokens) {
    if (allKeywords.some(k => k.includes(t))) score += 10;
  }

  // Description
  if (descLow.includes(queryNorm)) score += 18;
  const descHits = tokens.filter(t => descLow.includes(t)).length;
  score += descHits * 7;

  // Body text (only meaningful tokens, length >= 4)
  if (queryNorm.length >= 4 && bodyLow.includes(queryNorm)) score += 10;
  for (const t of tokens) {
    if (t.length >= 4 && bodyLow.includes(t)) score += 3;
  }

  return score;
}

// ── Public API ─────────────────────────────────────────────────────────────────

export function searchLearn(
  query: string,
  options: { type?: LearnItemType | ''; limit?: number } = {},
): LearnSearchItem[] {
  const queryNorm = query.trim().toLowerCase();
  if (!queryNorm) return [];

  const tokens = queryNorm.split(/\s+/).filter(t => t.length > 1);
  if (tokens.length === 0) return [];

  const { type = '', limit = 40 } = options;

  const scored = LEARN_INDEX
    .filter(item => !type || item.type === type)
    .map(item => ({ item, score: scoreItem(item, queryNorm, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => (b.score + b.item.priority) - (a.score + a.item.priority));

  return scored.slice(0, limit).map(r => r.item);
}

export function getLearnIndexStats(): { total: number; byType: Record<string, number> } {
  const byType: Record<string, number> = {};
  for (const item of LEARN_INDEX) {
    byType[item.type] = (byType[item.type] ?? 0) + 1;
  }
  return { total: LEARN_INDEX.length, byType };
}
